import {
  useMutation,
  useQueryClient,
  UseMutationResult,
} from "@tanstack/react-query"

import { useAuthedAxios } from "hooks/core/useAuthedAxios"
import { queryKeys } from "api/queryKeys"
import { HandledApiError } from "api/queryClient"
import { integrateData } from "api/Repos/data-integration"
import {
  IntegrateDataParams,
  IntegrateDataResponse,
  IntegrationFailure,
} from "types/dataIntegration.types"
import handleSequenceError from "utils/handleSequenceError"
import { notify } from "shared/ui/Notifications/notify"

/**
 * A merge that was cancelled because a derive script stopped part-way. Carries the
 * engine's script log so the modal can show WHICH line failed — the engine reports a
 * script error in its log only, never as an API error, so this detail is the only way
 * the user can act on it.
 */
export class IntegrationFailedError extends HandledApiError {
  readonly failure?: IntegrationFailure

  constructor(message: string, failure?: IntegrationFailure) {
    super(message)
    this.name = "IntegrationFailedError"
    this.failure = failure
  }
}

/**
 * React-query mutation for the Data Integration feature.
 *
 * Follows the Projects domain pattern: the mutationFn gates the response
 * through `handleSequenceError` (which notifies the user on a business error)
 * and throws `HandledApiError` so onError does not notify a second time. A
 * successful merge creates a new project, so onSuccess invalidates the projects
 * list. Call sites just `await mutateAsync(...)` and follow up (show the report,
 * navigate) — a rejection means the user has already been told what went wrong.
 */
export const useIntegrateDataMutation = (): UseMutationResult<
  IntegrateDataResponse,
  Error,
  IntegrateDataParams
> => {
  const getAxios = useAuthedAxios()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: IntegrateDataParams) => {
      const data = await integrateData(await getAxios(), params)
      if (!handleSequenceError(data, "Data Integration Failed!")) {
        // A cancelled merge carries a structured `failure` alongside the error
        // message; anything else is an ordinary business error.
        const failure = (data as { failure?: IntegrationFailure }).failure
        throw new IntegrationFailedError(
          "Sequence error integrating data",
          failure
        )
      }
      return data
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.root }),
    onError: (error: Error) => {
      if (error instanceof HandledApiError) return
      notify.error("Data Integration Failed!", error.message)
    },
  })
}
